/**
 * Issue #826 — the Value Dashboard's working reading view.
 *
 * The allowlisted slice of "how the trader was reading the board" that survives
 * a departure and return within this page lifetime. It is the dashboard's
 * counterpart to `GameDetail/readingView.ts`, riding the same #824 baseline
 * (`workingViewStore` / `useWorkingView`), and it obeys the same boundary:
 * reading controls only — never orders, arming, focus, tickets, market
 * membership or account data.
 *
 * Deliberately NOT here, because each already has a canonical owner that this
 * layer must not become a second reader/writer of: the Day/Week slate range,
 * the hidden-empty-sports list, Kalshi-markets-only, CFB Saved-only, minimized
 * games, density and the saved landing defaults — all `displaySettings` or a
 * dedicated store.
 */
import { dashboardSportFromParam, type DashboardSport } from '@/lib/routing/dashboardSport';
import type { WorkingViewDefinition } from '@/stores/workingViewStore';
import type { DiscoveryMode } from './discovery';
import type { DateFilter, MarketSide, MarketType, PriceMode, StatusFilter } from './types';

/** The board the toolbar is on: a dashboard sport key, the 'all' aggregate, or
 *  null for "no remembered board" — which falls back to the saved landing
 *  board. Mirrors exactly what `?sport=` can say, because the URL stays the
 *  authority whenever it says anything (see `dashboardReadingFromUrl`). */
export type ReadingSport = DashboardSport | 'all';

/**
 * The date the board is anchored on, as INTENT rather than a frozen day.
 *
 * `'today'` means the relative intent — resolved against the real current date
 * every time it is read, so a board left on today and returned to tomorrow
 * morning shows tomorrow's slate rather than a stale one. Anything else is a
 * bare ISO date the trader chose explicitly, and stays explicitly that date.
 *
 * The weekly slate (#712/#818) needs no representation here: it derives its
 * window from this anchor, and the Day/Week choice itself is a saved setting.
 */
export type DateAnchor = 'today' | string;

export interface DashboardReading {
  sport: ReadingSport | null;
  marketType: MarketType;
  dateAnchor: DateAnchor;
  dateFilter: DateFilter;
  statusFilter: StatusFilter;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /**
   * #754 — the COMMITTED overview search text, empty when the trader is not
   * searching. Reading state, not a market or an executable target: it narrows
   * which groups the board shows and nothing else.
   *
   * It lives here rather than in local state so that clicking a result through
   * to Game Detail and coming Back returns to the results the trader left,
   * which is the same promise the rest of this slice makes. It stays OUT of the
   * URL: `useWorkingView`'s record address includes `location.search`, so a
   * query parameter would mint a fresh record on every committed search.
   */
  discoveryQuery: string;
  /**
   * #754/#902 — Today / This week / Manual, the date scope the board is
   * reading ACROSS SPORTS. Reading state for the same reason the query above
   * is: a mode the trader chose has to survive opening a result and coming
   * Back, and it narrows which groups are on screen and nothing else.
   *
   * It is not a second date filter. `discoveryRange` resolves Today and This
   * week in the reader's own zone at read time, so a board left on Today and
   * returned to tomorrow shows tomorrow — the same relative-intent rule
   * `dateAnchor` follows. The saved manual sport and date stay exactly where
   * the trader left them underneath, which is what Back to my board restores.
   */
  discoveryMode: DiscoveryMode;
}

const MARKET_TYPES: MarketType[] = ['moneyline', 'spread', 'total', 'spread-total', 'series'];
const DATE_FILTERS: DateFilter[] = ['today', 'all'];
const STATUS_FILTERS: StatusFilter[] = ['active', 'all', 'settled'];
const MARKET_SIDES: MarketSide[] = ['yes', 'no'];
const PRICE_MODES: PriceMode[] = ['maker', 'taker', 'market'];
const DISCOVERY_MODES: DiscoveryMode[] = ['manual', 'today', 'week'];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A committed query is a few words a trader typed, not a document. The cap is
 *  a storage bound, not a validation rule — a longer stored value is truncated
 *  rather than rejected, because losing the whole reading slice over it would
 *  cost the trader their board. */
const MAX_QUERY_LENGTH = 200;

/** Today as a bare ISO date in the READER's own timezone — the same local-day
 *  notion `startOfIsoDayMs` compares game starts against. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The concrete day an anchor names right now. */
export function resolveDateAnchor(anchor: DateAnchor): string {
  return anchor === 'today' ? todayIso() : anchor;
}

/** The anchor to STORE for a chosen day. A day that is the current day is
 *  stored as the relative intent, whichever control chose it — arrows, picker
 *  or the Today chip — so "where I was" never decays into "the date it was
 *  when I left". */
export function toDateAnchor(iso: string): DateAnchor {
  return iso === todayIso() ? 'today' : iso;
}

function readSport(value: unknown): ReadingSport | null {
  if (value === 'all') return 'all';
  return typeof value === 'string' ? dashboardSportFromParam(value) : null;
}

export const dashboardReading: WorkingViewDefinition<DashboardReading> = {
  id: 'dashboard-reading-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<DashboardReading>;
    // A stored sport that is no longer a legal board key degrades to "no
    // remembered board" rather than rejecting the whole slice — the other
    // controls are still perfectly readable without it.
    const sport = v.sport == null ? null : readSport(v.sport);
    const dateAnchor = v.dateAnchor === 'today'
      || (typeof v.dateAnchor === 'string' && ISO_DATE.test(v.dateAnchor)) ? v.dateAnchor : null;
    if (dateAnchor === null
      || !MARKET_TYPES.includes(v.marketType as MarketType)
      || !DATE_FILTERS.includes(v.dateFilter as DateFilter)
      || !STATUS_FILTERS.includes(v.statusFilter as StatusFilter)
      || !MARKET_SIDES.includes(v.marketSide as MarketSide)
      || !PRICE_MODES.includes(v.priceMode as PriceMode)) return null;
    return {
      sport,
      marketType: v.marketType!,
      dateAnchor,
      dateFilter: v.dateFilter!,
      statusFilter: v.statusFilter!,
      marketSide: v.marketSide!,
      priceMode: v.priceMode!,
      // Absent or unreadable degrades to "not searching" rather than rejecting
      // the slice: every record written before #754 has no query at all, and
      // those boards must still resume.
      discoveryQuery: typeof v.discoveryQuery === 'string'
        ? v.discoveryQuery.slice(0, MAX_QUERY_LENGTH) : '',
      // Same degrade-rather-than-reject rule, and the same reason: every
      // record written before the Today/This week controls landed names no
      // mode at all, and those boards must still resume. Manual is the board
      // the trader set up themselves, so it is the safe reading of silence.
      discoveryMode: DISCOVERY_MODES.includes(v.discoveryMode as DiscoveryMode)
        ? v.discoveryMode! : 'manual',
    };
  },
};

/**
 * `?sport=` overlays the remembered board, synchronously, on every read.
 *
 * The sport filter is URL-owned (index.tsx's toolbar comment): reload and
 * shared links must land exactly where the URL says. Remembering it here only
 * answers the case the URL is SILENT about — the sidebar's bare `/dashboard`,
 * which used to drop the trader back on their saved landing board. A store
 * write can never overrule the parameter, because this overlay runs after it.
 */
export function dashboardReadingFromUrl(value: DashboardReading, search: string): DashboardReading {
  const param = new URLSearchParams(search).get('sport');
  // Absent is the only silent case. A param that is PRESENT but unreadable is
  // still the URL speaking, and it has always meant the landing board — so it
  // clears the remembered one rather than quietly leaving it in place.
  if (param === null) return value;
  return { ...value, sport: param === 'all' ? 'all' : dashboardSportFromParam(param) };
}

/** Exactly what `WorkingViewReset` promises to put back, for its tooltip. */
export const DASHBOARD_RESET_SCOPE =
  'market type, date, Today/All, status filter, YES/NO side, price mode, the '
  + 'overview search, the Today/This week discovery scope, and the sport board '
  + 'when the URL does not name one';
